import React, { useState } from 'react';
import './App.css';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Divider,
  Menu,
  MenuItem
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PhoneIcon from '@mui/icons-material/Phone';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EuroIcon from '@mui/icons-material/Euro';
import { Link, Routes, Route, Navigate } from 'react-router-dom';

import SEO from './components/SEO';
import Home from './pages/Home';
import Services from './pages/Services';
import Calculator from './pages/Calculator';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import MovingService from './pages/MovingService';
import BusinessMove from './pages/BusinessMove';
import PrivateMove from './pages/PrivateMove';
import InternationalMove from './pages/InternationalMove';
import JobcenterMove from './pages/JobcenterMove';
import ClearanceDisposal from './pages/ClearanceDisposal';
import MovingGuide from './pages/MovingGuide';
import MovingWithPets from './pages/MovingWithPets';
import MovingChecklist from './pages/MovingChecklist';
import MovingCosts from './pages/MovingCosts';
import MovingInsurance from './pages/MovingInsurance';

function App() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorElServices, setAnchorElServices] = useState(null);
  const [anchorElGuides, setAnchorElGuides] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const handleServicesClick = (event) => {
    setAnchorElServices(event.currentTarget);
  };

  const handleGuidesClick = (event) => {
    setAnchorElGuides(event.currentTarget);
  };

  const handleCloseServices = () => {
    setAnchorElServices(null);
  };

  const handleCloseGuides = () => {
    setAnchorElGuides(null);
  };

  const navItems = [
    { label: 'Start', to: '/' },
    { label: 'Umzugsservice', to: '/umzugsservice' },
    { label: 'Umzugskalkulator', to: '/calculator' },
    { label: 'Privatumzug', to: '/privatumzug' },
    { label: 'Firmenumzug', to: '/firmenumzug' },
    { label: 'Auslandsumzug', to: '/auslandsumzug' },
    { label: 'Jobcenterumzug', to: '/jobcenter-umzug' },
    { label: 'Entrümpelung und Entsorgung', to: '/entruempelung' },
    { label: 'Umzugsratgeber', to: '/ratgeber' },
    { label: 'Umzug mit Haustiere', to: '/umzug-mit-haustieren' },
    { label: 'Umzugscheckliste', to: '/umzugscheckliste' },
    { label: 'Umzugskosten', to: '/umzugskosten' },
    { label: 'Umzugsversicherung', to: '/umzugsversicherung' },
    { label: 'FAQ', to: '/faq' },
    { label: 'Kontakt', to: '/contact' }
  ];

  const serviceMenuItems = [
    { label: 'Umzugsservice', to: '/umzugsservice' },
    { label: 'Privatumzug', to: '/privatumzug' },
    { label: 'Firmenumzug', to: '/firmenumzug' },
    { label: 'Auslandsumzug', to: '/auslandsumzug' },
    { label: 'Jobcenterumzug', to: '/jobcenter-umzug' },
    { label: 'Entrümpelung', to: '/entruempelung' }
  ];

  const guideMenuItems = [
    { label: 'Umzugsratgeber', to: '/ratgeber' },
    { label: 'Umzug mit Haustiere', to: '/umzug-mit-haustieren' },
    { label: 'Umzugscheckliste', to: '/umzugscheckliste' },
    { label: 'Umzugskosten', to: '/umzugskosten' },
    { label: 'Umzugsversicherung', to: '/umzugsversicherung' }
  ];

  const drawer = (
    <Box sx={{ width: 250, bgcolor: '#275664', height: '100%', pt: 8 }}>
      <List>
        {navItems.map((item) => (
          <ListItemButton
            key={item.to}
            component={Link}
            to={item.to}
            onClick={() => setMobileOpen(false)}
          >
            <ListItemText primary={item.label} sx={{ color: 'white' }} />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  return (
    <div className="App">
      <SEO />
      {/* Header / Navigation */}
      <AppBar
        position="sticky"
        sx={{ backgroundColor: 'transparent', backdropFilter: 'blur(10px)', boxShadow: 'none' }}
      >
        <Toolbar sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          position: 'relative',
          padding: isMobile ? '8px 16px' : '0 24px',
          minHeight: isMobile ? 'auto' : '64px'
        }}>
          {isMobile ? (
            <>
              {/* First Row - Logo */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
                py: 1
              }}>
                <Link to="/" style={{ textDecoration: 'none' }}>
                  <img
                    src="/baran-final.png"
                    alt="H&N Umzugsservice"
                    style={{
                      width: '180px',
                      height: '150px',
                      borderRadius: '0',
                      objectFit: 'cover',
                      cursor: 'pointer'
                    }}
                  />
                </Link>
              </Box>
              
              {/* Second Row - Buttons */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                width: '100%',
                pb: 1
              }}>
                <IconButton
                  color="inherit"
                  aria-label="call"
                  href="tel:+4915563819185"
                  sx={{
                    color: 'white',
                    backgroundColor: '#275664',
                    padding: '12px',
                    '&:hover': {
                      backgroundColor: '#1a3a44'
                    }
                  }}
                >
                  <PhoneIcon sx={{ fontSize: '32px' }} />
                </IconButton>
                <Button
                  variant="contained"
                  component={Link}
                  to="/calculator"
                  startIcon={
                    <img
                      src="/calculator.png"
                      alt="calculator"
                      style={{ width: '24px', height: '28px' }}
                    />
                  }
                  sx={{
                    backgroundColor: '#275664',
                    color: 'white',
                    borderRadius: '20px',
                    px: 2.5,
                    py: 1.5,
                    fontSize: '0.875rem',
                    textTransform: 'none',
                    minHeight: '52px',
                    '&:hover': {
                      backgroundColor: '#1a3a44'
                    }
                  }}
                >
                  Umzug berechnen
                </Button>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={() => setMobileOpen(true)}
                  sx={{
                    color: 'white',
                    padding: '12px'
                  }}
                >
                  <MenuIcon sx={{ fontSize: '40px' }} />
                </IconButton>
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', color: 'white' }}>
                  <img
                    src="/baran-final.png"
                    alt="H&N Umzugsservice"
                    style={{
                      width: '100px',
                      height: '100px',
                      marginRight: '10px',
                      borderRadius: '0',
                      objectFit: 'cover',
                      cursor: 'pointer'
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                      color: 'white'
                    }}
                  >
                    Umzugsservice
                  </Typography>
                </Link>
              </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                color="inherit"
                component={Link}
                to="/"
                sx={{ mx: 0.5 }}
              >
                Start
              </Button>
              
              <Button
                color="inherit"
                onClick={handleServicesClick}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{ mx: 0.5 }}
              >
                Services
              </Button>
              <Menu
                anchorEl={anchorElServices}
                open={Boolean(anchorElServices)}
                onClose={handleCloseServices}
                sx={{ mt: 1 }}
              >
                {serviceMenuItems.map((item) => (
                  <MenuItem
                    key={item.to}
                    component={Link}
                    to={item.to}
                    onClick={handleCloseServices}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Menu>

              <Button
                color="inherit"
                component={Link}
                to="/calculator"
                sx={{ mx: 0.5 }}
              >
                Kalkulator
              </Button>

              <Button
                color="inherit"
                onClick={handleGuidesClick}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{ mx: 0.5 }}
              >
                Ratgeber
              </Button>
              <Menu
                anchorEl={anchorElGuides}
                open={Boolean(anchorElGuides)}
                onClose={handleCloseGuides}
                sx={{ mt: 1 }}
              >
                {guideMenuItems.map((item) => (
                  <MenuItem
                    key={item.to}
                    component={Link}
                    to={item.to}
                    onClick={handleCloseGuides}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Menu>

              <Button
                color="inherit"
                component={Link}
                to="/faq"
                sx={{ mx: 0.5 }}
              >
                FAQ
              </Button>

              <Button
                color="inherit"
                component={Link}
                to="/contact"
                sx={{ mx: 0.5 }}
              >
                Kontakt
              </Button>
            </Box>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            bgcolor: '#275664',
            width: 250
          }
        }}
      >
        {drawer}
      </Drawer>

      {/* Routed Content */}
      <Box>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/calculator" element={<Calculator />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/umzugsservice" element={<MovingService />} />
          <Route path="/privatumzug" element={<PrivateMove />} />
          <Route path="/firmenumzug" element={<BusinessMove />} />
          <Route path="/auslandsumzug" element={<InternationalMove />} />
          <Route path="/jobcenter-umzug" element={<JobcenterMove />} />
          <Route path="/entruempelung" element={<ClearanceDisposal />} />
          <Route path="/ratgeber" element={<MovingGuide />} />
          <Route path="/umzug-mit-haustieren" element={<MovingWithPets />} />
          <Route path="/umzugscheckliste" element={<MovingChecklist />} />
          <Route path="/umzugskosten" element={<MovingCosts />} />
          <Route path="/umzugsversicherung" element={<MovingInsurance />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: '#0a1f28', py: 4 }}>
        <Container maxWidth="lg">
          <Divider sx={{ bgcolor: '#55d6dc', mb: 3 }} />
          <Typography variant="body2" align="center" sx={{ color: 'white', opacity: 0.7 }}>
            © 2025 H&N Umzugsservice. Alle Rechte vorbehalten.
          </Typography>
          <Typography variant="body2" align="center" sx={{ color: 'white', opacity: 0.5, mt: 1 }}>
            Impressum | Datenschutz | AGB
          </Typography>
          <Typography variant="body2" align="center" sx={{ color: 'white', opacity: 0.5, mt: 1 }}>
            Design und Entwicklung von <a href="https://scope-code.com" style={{ color: '#55d6dc', textDecoration: 'none', fontWeight: 'bold' }} target="_blank" rel="noopener noreferrer">scope-code.com</a>
          </Typography>
        </Container>
      </Box>
    </div>
  );
}

export default App;